import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  STAGE_COLORS,
  ChartCard,
  KpiCard,
  Notes,
  formatNumber,
  formatPercent,
  tooltipProps,
} from '../components/ChartKit';
import { SegmentList } from './OverviewTab';

const STEP_COLORS = {
  saved: '#6366f1',
  leads: '#2563eb',
  contacted: '#0284c7',
  qualified: '#4f46e5',
  meeting: '#d97706',
  proposal: '#7c3aed',
  won: '#16a34a',
};

/** FR-M13-002 Funnel analytics: conversion, time in stage, leakage */
const FunnelTab = ({ data, periodLabel }) => {
  const max = Math.max(1, ...data.steps.map((s) => s.count));
  const pipelineStep = data.steps.find((s) => s.key === 'leads');
  const wonStep = data.steps.find((s) => s.key === 'won');
  const slowest = [...data.timeInStage]
    .filter((t) => t.averageDays !== null)
    .sort((a, b) => b.averageDays - a.averageDays)[0];
  const timeData = data.timeInStage.filter((t) => t.averageDays !== null);

  return (
    <div className="analytics-tab-body">
      <div className="analytics-kpis">
        <KpiCard
          icon="fa-user-plus"
          label="Leads added"
          value={formatNumber(pipelineStep?.count)}
          hint={periodLabel}
        />
        <KpiCard
          icon="fa-trophy"
          label="Lead → won"
          value={formatPercent(wonStep?.conversionFromStart)}
          hint={`${formatNumber(wonStep?.count)} of these leads won`}
          tone="green"
        />
        <KpiCard
          icon="fa-hourglass-half"
          label="Slowest stage"
          value={slowest ? slowest.stage : '—'}
          hint={
            slowest
              ? `${formatNumber(slowest.averageDays, 1)} days on average`
              : 'Not enough history'
          }
          tone="amber"
        />
        <KpiCard
          icon="fa-arrow-trend-down"
          label="Leads lost"
          value={formatNumber(data.leakage.totalLost)}
          hint={periodLabel}
          tone="red"
        />
      </div>

      <div className="analytics-grid">
        <ChartCard
          wide
          title="Conversion funnel"
          subtitle="How far leads added in this period have progressed"
          empty={!pipelineStep?.count}
          emptyText="No leads were added to the pipeline in this period."
          table={{
            columns: ['Step', 'Count', 'From previous step', 'From pipeline'],
            rows: data.steps.map((s) => [
              s.label,
              s.count,
              formatPercent(s.conversionFromPrevious),
              formatPercent(s.conversionFromStart),
            ]),
          }}
        >
          <ol className="analytics-funnel">
            {data.steps.map((s, i) => {
              const color = STEP_COLORS[s.key] || '#2563eb';
              const widthPct = Math.max(2, (s.count / max) * 100);

              return (
                <li key={s.key} className="analytics-funnel-row">
                  <div className="analytics-funnel-label">
                    <span className="analytics-funnel-step-num">{i + 1}</span>
                    <span className="analytics-funnel-name">{s.label}</span>
                    {i > 0 && s.conversionFromPrevious !== null && (
                      <span className="analytics-funnel-rate">
                        {formatPercent(s.conversionFromPrevious)} from previous
                      </span>
                    )}
                  </div>
                  <div className="analytics-funnel-track">
                    <div
                      className="analytics-funnel-fill"
                      style={{
                        width: `${widthPct}%`,
                        background: color,
                      }}
                    />
                  </div>
                  <div className="analytics-funnel-values">
                    <span className="analytics-funnel-count">{formatNumber(s.count)}</span>
                    {s.conversionFromStart !== null && (
                      <span className="analytics-funnel-pct">
                        {formatPercent(s.conversionFromStart)}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </ChartCard>

        <ChartCard
          title="Average time in each stage"
          subtitle="Days spent before moving on (from recorded stage changes)"
          empty={!timeData.length}
          emptyText="Not enough stage history yet. Move leads between stages to see this."
          table={{
            columns: ['Stage', 'Average days', 'Leads measured'],
            rows: data.timeInStage.map((t) => [
              t.stage,
              t.averageDays === null ? 'Not enough data' : formatNumber(t.averageDays, 1),
              t.sampleSize,
            ]),
          }}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={timeData}
              layout="vertical"
              margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} unit=" d" />
              <YAxis type="category" dataKey="stage" tick={{ fontSize: 11 }} width={90} />
              <Tooltip
                {...tooltipProps}
                formatter={(v) => [`${formatNumber(v, 1)} days`, 'Average']}
              />
              <Bar dataKey="averageDays" name="Average days" radius={[0, 4, 4, 0]}>
                {timeData.map((t) => (
                  <Cell key={t.stage} fill={STAGE_COLORS[t.stage] || '#2563eb'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Where leads were lost"
          subtitle="The stage each lost lead was in before it was lost"
          empty={!data.leakage.byStage.length}
          emptyText="No leads were lost in this period."
          table={{
            columns: ['Stage', 'Leads lost'],
            rows: data.leakage.byStage.map((s) => [s.stage, s.count]),
          }}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={data.leakage.byStage}
              margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="stage" tick={{ fontSize: 11 }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip {...tooltipProps} />
              <Bar dataKey="count" name="Leads lost" fill="#dc2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <SegmentList title="Top reasons for losing" items={data.leakage.topReasons} />
      </div>

      <Notes items={data.notes} />
    </div>
  );
};

export default FunnelTab;
